import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { queryKeys, QUERY_CONFIG } from '../../lib/react-query'
import { loadTestApi } from '../../services/api/loadTest.api'
import { useAuthStore } from '../../store/authStore'

// No automatic retry anywhere below: 401/403/404 are real answers (a user without the
// permission will see 403 here), and a retry only delays the screen that explains it.

/**
 * The paginated load tests list. Reads the URL
 * for filters, ordering and page; `latest` defaults to `true` (newest test per asset, URL
 * and VU level) and `latest=false` is the full history.
 */
export function useLoadTests() {
  const orgId = useAuthStore((s) => s.orgId)
  const [searchParams] = useSearchParams()
  const next = new URLSearchParams(searchParams)
  if (!next.has('latest')) next.set('latest', 'true')
  const params = next.toString()
  return useQuery({
    queryKey: queryKeys.loadTest.list(orgId, params),
    queryFn: () => loadTestApi.getList(params),
    enabled: !!orgId,
    retry: false,
  })
}

/** Facet options. */
export function useLoadTestFilterOptions() {
  const orgId = useAuthStore((s) => s.orgId)
  return useQuery({
    queryKey: queryKeys.loadTest.filterOptions(orgId),
    queryFn: () => loadTestApi.getFilterOptions(),
    staleTime: QUERY_CONFIG.LISTS.staleTime,
    enabled: !!orgId,
    retry: false,
  })
}

/** One load test with thresholds and the per-second timeline. */
export function useLoadTestDetail(id: string | null) {
  const orgId = useAuthStore((s) => s.orgId)
  return useQuery({
    queryKey: queryKeys.loadTest.detail(orgId, id || ''),
    queryFn: () => loadTestApi.getById(id!),
    enabled: !!id && !!orgId,
    retry: false,
  })
}
